import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { IoClose } from "react-icons/io5";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import VuiTypography from "components/VuiTypography";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { PERMISSIONS } from "auth/permissions";
import {
  addSSOAllowedEmail,
  getSSOSettings,
  listSSOAllowedEmails,
  removeSSOAllowedEmail,
  saveSSOSettings,
  updateSSOAllowedEmailRole,
  generateScimToken,
} from "services/tenantUserAdminService";
import { TC } from "../../tokens/text";

const CARD_BG = "#ffffff";
const CARD_BORDER = "1px solid #d8e6f7";

const inputSx = {
  "& .MuiInputBase-root": {
    color: TC.primary,
    background: "#f4f8ff",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": { color: TC.secondary },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d8e6f7" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#7a94b0" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0075ff" },
};

const defaultSettings = {
  enabled: false,
  allowPasswordFallback: true,
  providerType: "google",
  providerConfig: {},
};

const defaultAddForm = {
  email: "",
  displayName: "",
  role: "user",
};

const ROLE_OPTIONS = [
  { value: "tenant_admin", label: "Tenant Admin" },
  { value: "user", label: "User" },
];

function normalizeSettings(settings = {}) {
  const source = settings?.settings && typeof settings.settings === "object" ? settings.settings : settings;

  return {
    enabled: Boolean(source.enabled ?? source.ssoEnabled ?? false),
    allowPasswordFallback: Boolean(source.allowPasswordFallback ?? source.passwordFallbackEnabled ?? true),
    providerType: `${source.providerType || source.provider || "google"}`,
    providerConfig: typeof source.providerConfig === 'object' ? source.providerConfig : (source.config && typeof source.config === 'object' ? source.config : {}),
    scimAutoAllow: Boolean(source.scimAutoAllow ?? true),
  };
}

function normalizeAllowedEmails(response) {
  const source = Array.isArray(response)
    ? response
    : Array.isArray(response?.allowedEmails)
      ? response.allowedEmails
      : Array.isArray(response?.emails)
        ? response.emails
        : Array.isArray(response?.items)
          ? response.items
          : [];

  return source
    .map((entry) => ({
      email: `${entry?.email || ""}`.trim(),
      displayName: `${entry?.displayName || entry?.name || ""}`.trim(),
      role: `${entry?.role || "user"}`.trim() || "user",
    }))
    .filter((entry) => entry.email);
}

function sortAllowedEmails(entries) {
  return [...entries].sort((left, right) => left.email.localeCompare(right.email));
}

function getRoleLabel(role) {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label || role;
}

function SSOSettings() {
  const { isSuperAdmin, isTenantAdmin, tenantId, hasAllPermissions } = useAuthz();
  const { companies, selectedCompanyId } = useCompany();
  const activeTenantId = isSuperAdmin ? selectedCompanyId || "" : tenantId || "";
  const activeTenant = useMemo(
    () => (Array.isArray(companies) ? companies.find((company) => company.id === activeTenantId) : null),
    [companies, activeTenantId]
  );

  const [settings, setSettings] = useState(defaultSettings);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [emailsError, setEmailsError] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [addForm, setAddForm] = useState(defaultAddForm);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [rowLoadingState, setRowLoadingState] = useState({});

  // SCIM token generation state
  const [isGeneratingScimToken, setIsGeneratingScimToken] = useState(false);
  const [scimToken, setScimToken] = useState("");
  const [scimModalOpen, setScimModalOpen] = useState(false);

  const canManageSSO = hasAllPermissions([PERMISSIONS.MANAGE_SSO]);

  const handleGenerateScimToken = async () => {
    if (!activeTenantId) return;
    setIsGeneratingScimToken(true);
    setStatus({ type: "", message: "" });
    try {
      const resp = await generateScimToken(activeTenantId);
      const token = resp?.token || resp;
      setScimToken(token);
      setScimModalOpen(true);
      setStatus({ type: "success", message: "SCIM token generated. Copy it now; it will not be shown again." });
    } catch (error) {
      setStatus({ type: "error", message: error?.message || "Failed to generate SCIM token." });
    } finally {
      setIsGeneratingScimToken(false);
    }
  };


  const loadPageData = useCallback(async (isMounted = () => true) => {
    if (!activeTenantId) {
      if (!isMounted()) return;
      setSettings(defaultSettings);
      setAllowedEmails([]);
      setSettingsLoading(false);
      setEmailsLoading(false);
      setSettingsError("");
      setEmailsError("");
      return;
    }

    setSettingsLoading(true);
    setEmailsLoading(true);
    setSettingsError("");
    setEmailsError("");
    setStatus({ type: "", message: "" });

    const [settingsResult, allowedEmailsResult] = await Promise.allSettled([
      getSSOSettings(activeTenantId),
      listSSOAllowedEmails(activeTenantId),
    ]);

    if (!isMounted()) return;

    if (settingsResult.status === "fulfilled") {
      setSettings(normalizeSettings(settingsResult.value));
    } else {
      setSettings(defaultSettings);
      setSettingsError(settingsResult.reason?.message || "Failed to load SSO settings.");
    }
    setSettingsLoading(false);

    if (allowedEmailsResult.status === "fulfilled") {
      setAllowedEmails(sortAllowedEmails(normalizeAllowedEmails(allowedEmailsResult.value)));
    } else {
      setAllowedEmails([]);
      setEmailsError(allowedEmailsResult.reason?.message || "Failed to load allowed SSO emails.");
    }
    setEmailsLoading(false);
  }, [activeTenantId]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      try {
        await loadPageData(() => active);
      } catch (error) {
        if (!active) return;
        setSettingsLoading(false);
        setEmailsLoading(false);
        setSettingsError(error?.message || "Failed to load SSO settings.");
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [loadPageData]);

  const handleSettingsSwitchChange = (field) => (_, checked) => {
    setSettings((prev) => ({ ...prev, [field]: checked }));
  };

  const handleAddFormChange = (field) => (event) => {
    const value = event?.target?.value || "";
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    if (!activeTenantId) return;

    setIsSavingSettings(true);
    setStatus({ type: "", message: "" });
    try {
      const providerType = settings.providerType || "google";
      const providerName = providerType === "azure" ? "Azure SAML" : providerType === "google" ? "Google" : providerType;

      const payload = {
        enabled: Boolean(settings.enabled),
        providerType,
        providerName,
        allowPasswordFallback: Boolean(settings.allowPasswordFallback),
        // tenant-level toggle: auto-allow SCIM-provisioned users into the SSO allow list
        scimAutoAllow: Boolean(settings.scimAutoAllow),
        providerConfig: settings.providerConfig || {},
      };

      const saved = await saveSSOSettings(activeTenantId, payload);
      setSettings(normalizeSettings(saved && Object.keys(saved).length ? saved : payload));
      setStatus({ type: "success", message: `${providerName} SSO settings saved successfully.` });
    } catch (error) {
      setStatus({ type: "error", message: error?.message || "Failed to save SSO settings." });
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Generate a minimal SP metadata XML for tenants to upload to their IdP
  function generateSPMetadata(entityId, acsUrl, sloUrl) {
    return `<?xml version="1.0" encoding="UTF-8"?>\n<EntityDescriptor xmlns="urn:oasis:names:tc:SAML:2.0:metadata" entityID="${entityId}">\n  <SPSSODescriptor AuthnRequestsSigned="false" WantAssertionsSigned="true" protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">\n    <NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</NameIDFormat>\n    <AssertionConsumerService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST" Location="${acsUrl}" index="1"/>\n    ${sloUrl ? `<SingleLogoutService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect" Location="${sloUrl}"/>` : ''}\n  </SPSSODescriptor>\n</EntityDescriptor>`;
  }

  const downloadMetadata = () => {
    try {
      const entityId = (settings?.providerConfig?.spEntityId || process.env.REACT_APP_CANONICAL_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
      const acsBase = (settings?.providerConfig?.spAcsBase || process.env.REACT_APP_ACCOUNTING_API_BASE_URL || 'http://localhost:4001').replace(/\/$/, '');
      const acs = `${acsBase}/sso/azure-acs?tenant=${encodeURIComponent(activeTenantId)}`;
      const slo = `${acsBase}/sso/azure-logout?tenant=${encodeURIComponent(activeTenantId)}`;
      const xml = generateSPMetadata(entityId, acs, slo);
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledgerhub-saml-metadata-${activeTenantId || 'sp'}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus({ type: 'success', message: 'SP metadata downloaded.' });
    } catch (e) {
      setStatus({ type: 'error', message: 'Failed to generate metadata.' });
    }
  };

  const copyMetadataToClipboard = async () => {
    try {
      const entityId = (settings?.providerConfig?.spEntityId || process.env.REACT_APP_CANONICAL_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
      const acsBase = (settings?.providerConfig?.spAcsBase || process.env.REACT_APP_ACCOUNTING_API_BASE_URL || 'http://localhost:4001').replace(/\/$/, '');
      const acs = `${acsBase}/sso/azure-acs?tenant=${encodeURIComponent(activeTenantId)}`;
      const slo = `${acsBase}/sso/azure-logout?tenant=${encodeURIComponent(activeTenantId)}`;
      const xml = generateSPMetadata(entityId, acs, slo);
      await navigator.clipboard.writeText(xml);
      setStatus({ type: 'success', message: 'SP metadata copied to clipboard.' });
    } catch (e) {
      setStatus({ type: 'error', message: 'Failed to copy metadata to clipboard.' });
    }
  };

  const handleAddAllowedEmail = async () => {
    if (!activeTenantId || isAddingEmail) return;

    const email = addForm.email.trim().toLowerCase();
    const displayName = addForm.displayName.trim();

    if (!email) {
      setStatus({ type: "error", message: "Email is required." });
      return;
    }

    setIsAddingEmail(true);
    setStatus({ type: "", message: "" });
    try {
      const created = await addSSOAllowedEmail(activeTenantId, {
        email,
        role: addForm.role,
        displayName,
      });
      const createdEntry = normalizeAllowedEmails(created)[0] || { email, role: addForm.role, displayName };
      setAllowedEmails((prev) =>
        sortAllowedEmails([...prev.filter((entry) => entry.email !== createdEntry.email), createdEntry])
      );
      setAddForm(defaultAddForm);
      setStatus({ type: "success", message: `Added ${email} to the SSO allow list.` });
    } catch (error) {
      setStatus({ type: "error", message: error?.message || "Failed to add allowed email." });
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleRoleChange = async (email, role) => {
    if (!activeTenantId || !email) return;

    setRowLoadingState((prev) => ({ ...prev, [email]: true }));
    setStatus({ type: "", message: "" });
    try {
      await updateSSOAllowedEmailRole(activeTenantId, email, role);
      setAllowedEmails((prev) =>
        sortAllowedEmails(prev.map((entry) => (entry.email === email ? { ...entry, role } : entry)))
      );
      setStatus({ type: "success", message: `Updated role for ${email}.` });
    } catch (error) {
      setStatus({ type: "error", message: error?.message || "Failed to update allowed email role." });
    } finally {
      setRowLoadingState((prev) => ({ ...prev, [email]: false }));
    }
  };

  const handleRemoveEmail = async (email) => {
    if (!activeTenantId || !email) return;

    setRowLoadingState((prev) => ({ ...prev, [email]: true }));
    setStatus({ type: "", message: "" });
    try {
      await removeSSOAllowedEmail(activeTenantId, email);
      setAllowedEmails((prev) => prev.filter((entry) => entry.email !== email));
      setStatus({ type: "success", message: `Removed ${email} from the SSO allow list.` });
    } catch (error) {
      setStatus({ type: "error", message: error?.message || "Failed to remove allowed email." });
    } finally {
      setRowLoadingState((prev) => ({ ...prev, [email]: false }));
    }
  };

  if ((!isSuperAdmin && !isTenantAdmin) || !canManageSSO) {
    return <Redirect to="/unauthorized" />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="lg" sx={{ color: TC.primary }}>
            SSO Settings
          </VuiTypography>
          <VuiTypography variant="button" sx={{ color: TC.secondary }}>
            Configure tenant SSO provider and manage the allow list for this tenant.
          </VuiTypography>
          {activeTenantId ? (
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary, mt: 1 }}>
              Active tenant: {activeTenant?.name || activeTenantId}
            </VuiTypography>
          ) : null}
        </VuiBox>

        {status.message ? (
          <Alert severity={status.type || "info"} sx={{ mb: 3 }}>
            {status.message}
          </Alert>
        ) : null}

        {!activeTenantId ? (
          <Card sx={{ background: CARD_BG, border: CARD_BORDER }}>
            <VuiBox p={3}>
              <VuiTypography sx={{ color: TC.primary }}>
                Select a tenant to manage SSO settings.
              </VuiTypography>
            </VuiBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, height: "100%" }}>
                <VuiBox p={3}>
                  <VuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                    mb={2}
                  >
                    <VuiBox>
                      <VuiTypography sx={{ color: TC.primary, fontWeight: 700 }}>
                        SSO Configuration
                      </VuiTypography>
                      <VuiTypography variant="button" sx={{ color: TC.secondary }}>
                        Configure tenant-wide SSO provider and options.
                      </VuiTypography>
                    </VuiBox>
                    <VuiButton color="info" onClick={handleSaveSettings} disabled={settingsLoading || isSavingSettings}>
                      {isSavingSettings ? "Saving..." : "Save Settings"}
                    </VuiButton>
                  </VuiBox>

                  {settingsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {settingsError}
                    </Alert>
                  ) : null}

                  {settingsLoading ? (
                    <LoadingSpinner label="Loading SSO settings…" />
                  ) : (
                    <>
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            fullWidth
                            label="Provider"
                            value={settings.providerType || "google"}
                            onChange={(e) => setSettings((prev) => ({ ...prev, providerType: e.target.value }))}
                            sx={inputSx}
                          >
                            <MenuItem value="google">Google</MenuItem>
                            <MenuItem value="azure">Azure SAML</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <VuiBox display="flex" alignItems="center" gap={1}>
                            <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>{settings.providerType === 'azure' ? 'Enable Azure SSO' : 'Enable Google SSO'}</VuiTypography>
                            <VuiSwitch color="info" checked={settings.enabled} onChange={handleSettingsSwitchChange("enabled")} />
                          </VuiBox>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, display: 'block' }}>
                            {settings.providerType === 'azure' ? 'Allow users to sign in using Azure AD SAML.' : 'Let approved users sign in with their Google account.'}
                          </VuiTypography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ borderColor: "#d8e6f7", my: 2 }} />

                      <VuiBox mb={2}>
                        <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          <VuiBox>
                            <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>
                              Allow password fallback
                            </VuiTypography>
                            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                              Keep email and password sign-in available for fallback access.
                            </VuiTypography>
                          </VuiBox>
                          <VuiSwitch color="info" checked={settings.allowPasswordFallback} onChange={handleSettingsSwitchChange("allowPasswordFallback")} />
                        </VuiBox>
                      </VuiBox>

                      <VuiBox mb={2}>
                        <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          <VuiBox>
                            <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>
                              Auto-allow SCIM-provisioned users
                            </VuiTypography>
                            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                              When enabled, users created or updated via SCIM are automatically added to the tenant SSO allow list with the default role 'user'. Disable to require manual approval.
                            </VuiTypography>
                          </VuiBox>
                          <VuiSwitch color="info" checked={settings.scimAutoAllow} onChange={handleSettingsSwitchChange("scimAutoAllow")} />
                        </VuiBox>
                      </VuiBox>

                      {settings.providerType === 'azure' && (
                        <>
                          <Divider sx={{ borderColor: "#d8e6f7", my: 2 }} />
                          <VuiTypography sx={{ color: TC.primary, fontWeight: 600, mb: 1 }}>Azure SAML Configuration</VuiTypography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField fullWidth label="IdP Entity ID" value={settings.providerConfig?.idpEntityId || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), idpEntityId: e.target.value } }))} sx={inputSx} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField fullWidth label="IdP SSO URL" value={settings.providerConfig?.idpSsoUrl || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), idpSsoUrl: e.target.value } }))} sx={inputSx} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField fullWidth multiline minRows={4} label="IdP Certificate (X.509)" value={settings.providerConfig?.idpCertificate || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), idpCertificate: e.target.value } }))} sx={inputSx} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField fullWidth label="IdP Metadata URL (optional)" value={settings.providerConfig?.metadataUrl || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), metadataUrl: e.target.value } }))} sx={inputSx} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField fullWidth label="SP Entity ID (optional)" value={settings.providerConfig?.spEntityId || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), spEntityId: e.target.value } }))} sx={inputSx} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField fullWidth label="SP ACS base URL (optional)" value={settings.providerConfig?.spAcsBase || ""} onChange={(e) => setSettings((prev) => ({ ...prev, providerConfig: { ...(prev.providerConfig || {}), spAcsBase: e.target.value } }))} sx={inputSx} />
                            </Grid>

                            <Grid item xs={12}>
                              <VuiBox display="flex" gap={1} alignItems="center">
                                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Assertion Consumer Service (ACS) URL:</VuiTypography>
                                <VuiTypography variant="caption" sx={{ color: TC.link, wordBreak: 'break-all' }}>
                                  {((settings?.providerConfig?.spAcsBase || process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001").replace(/\/$/, "")) + `/sso/azure-acs?tenant=${encodeURIComponent(activeTenantId)}`}
                                </VuiTypography>
                                <VuiButton size="small" color="white" onClick={() => { try { const acs = ((settings?.providerConfig?.spAcsBase || process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001").replace(/\/$/, "")) + `/sso/azure-acs?tenant=${encodeURIComponent(activeTenantId)}`; navigator.clipboard.writeText(acs); setStatus({ type: 'success', message: 'ACS URL copied to clipboard.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy ACS URL.' }); } }}>Copy</VuiButton>
                              </VuiBox>
                            </Grid>

                            <Grid item xs={12}>
                              <VuiBox display="flex" gap={1} alignItems="center" mt={1}>
                                <VuiButton size="small" color="info" onClick={downloadMetadata}>Download metadata</VuiButton>
                                <VuiButton size="small" color="white" onClick={copyMetadataToClipboard}>Copy metadata</VuiButton>
                                {settings.providerType === 'azure' && canManageSSO ? (
                                  <VuiButton size="small" color="warning" onClick={handleGenerateScimToken} disabled={isGeneratingScimToken}>
                                    {isGeneratingScimToken ? 'Generating...' : 'Generate SCIM token'}
                                  </VuiButton>
                                ) : null}
                              </VuiBox>

                              {/* SCIM token quick-view and actions: show freshly generated token, or indicate stored token */}
                              <VuiBox mt={1}>
                                {scimToken ? (
                                  <VuiBox display="flex" gap={1} alignItems="center">
                                    <VuiBox sx={{ background: '#f4f8ff', p: 1, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>SCIM token (temporary display)</VuiTypography>
                                      <VuiTypography variant="button" sx={{ color: TC.primary }}>{scimToken}</VuiTypography>
                                    </VuiBox>
                                    <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(scimToken || ''); setStatus({ type: 'success', message: 'SCIM token copied to clipboard.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy token.' }); } }}>Copy</VuiButton>
                                  </VuiBox>
                                ) : settings.providerConfig?.scimBearerToken ? (
                                  <VuiBox display="flex" gap={1} alignItems="center">
                                    <VuiBox sx={{ background: '#f9fafb', p: 1, borderRadius: 1, flex: 1 }}>
                                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>SCIM token stored (hidden for security). Regenerate to replace it.</VuiTypography>
                                    </VuiBox>
                                    <VuiButton size="small" color="warning" onClick={handleGenerateScimToken} disabled={isGeneratingScimToken}>{isGeneratingScimToken ? 'Generating...' : 'Regenerate token'}</VuiButton>
                                  </VuiBox>
                                ) : null}
                              </VuiBox>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </VuiBox>
              </Card>

              {/* Basic SAML configuration helper */}
              <VuiBox mt={2} />
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, mt: 2 }}>
                <VuiBox p={3}>
                  <VuiTypography sx={{ color: TC.primary, fontWeight: 700, mb: 1 }}>Basic SAML Configuration</VuiTypography>
                  <VuiTypography variant="button" sx={{ color: TC.secondary, display: 'block', mb: 2 }}>
                    Use these values when configuring Azure AD (or other IdPs). Click "Copy" to copy a value to your clipboard.
                  </VuiTypography>
                  <VuiBox display="flex" flexDirection="column" gap={1}>
                    <VuiBox display="flex" justifyContent="flex-end" gap={1} mb={1}>
                      <VuiButton size="small" color="info" onClick={downloadMetadata}>Download metadata</VuiButton>
                      <VuiButton size="small" color="white" onClick={copyMetadataToClipboard}>Copy metadata</VuiButton>
                    </VuiBox>
                    {(() => {
                                          // Compute the canonical app URL (prefer per-tenant override, then env)
                                          const entityId = (settings?.providerConfig?.spEntityId || process.env.REACT_APP_CANONICAL_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
                                          const acsBase = (settings?.providerConfig?.spAcsBase || process.env.REACT_APP_ACCOUNTING_API_BASE_URL || 'http://localhost:4001').replace(/\/$/, '');
                                          const acs = `${acsBase}/sso/azure-acs?tenant=${encodeURIComponent(activeTenantId)}`;
                                          const signOn = entityId ? entityId + '/authentication/sign-in' : '/authentication/sign-in';
                      const relay = '';
                                          const logout = `${acsBase}/sso/azure-logout?tenant=${encodeURIComponent(activeTenantId)}`;
                      return (
                        <>
                          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                            <VuiBox>
                              <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>Identifier (Entity ID)</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, wordBreak: 'break-all' }}>{entityId}</VuiTypography>
                            </VuiBox>
                            <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(entityId); setStatus({ type: 'success', message: 'Entity ID copied.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy.' }); } }}>Copy</VuiButton>
                          </VuiBox>

                          <Divider sx={{ borderColor: '#d8e6f7', my: 1 }} />

                          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                            <VuiBox>
                              <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>Reply URL (ACS)</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, wordBreak: 'break-all' }}>{acs}</VuiTypography>
                            </VuiBox>
                            <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(acs); setStatus({ type: 'success', message: 'ACS URL copied.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy ACS URL.' }); } }}>Copy</VuiButton>
                          </VuiBox>

                          <Divider sx={{ borderColor: '#d8e6f7', my: 1 }} />

                          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                            <VuiBox>
                              <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>Sign on URL</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, wordBreak: 'break-all' }}>{signOn}</VuiTypography>
                            </VuiBox>
                            <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(signOn); setStatus({ type: 'success', message: 'Sign-on URL copied.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy Sign-on URL.' }); } }}>Copy</VuiButton>
                          </VuiBox>

                          <Divider sx={{ borderColor: '#d8e6f7', my: 1 }} />

                          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                            <VuiBox>
                              <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>Relay State (Optional)</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, wordBreak: 'break-all' }}>{relay || 'Optional'}</VuiTypography>
                            </VuiBox>
                            <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(relay || ''); setStatus({ type: 'success', message: 'Relay State copied.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy Relay State.' }); } }}>Copy</VuiButton>
                          </VuiBox>

                          <Divider sx={{ borderColor: '#d8e6f7', my: 1 }} />

                          <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                            <VuiBox>
                              <VuiTypography sx={{ color: TC.primary, fontWeight: 600 }}>Logout URL (Optional)</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, wordBreak: 'break-all' }}>{logout}</VuiTypography>
                            </VuiBox>
                            <VuiButton size="small" color="white" onClick={() => { try { navigator.clipboard.writeText(logout); setStatus({ type: 'success', message: 'Logout URL copied.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy Logout URL.' }); } }}>Copy</VuiButton>
                          </VuiBox>
                        </>
                      );
                    })()}
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, height: "100%" }}>
                <VuiBox p={3}>
                  <VuiTypography sx={{ color: TC.primary, fontWeight: 700, mb: 0.5 }}>
                    Allowed SSO Emails
                  </VuiTypography>
                  <VuiTypography variant="button" sx={{ color: TC.secondary }}>
                    Add approved Google accounts and assign their LedgerHub role.
                  </VuiTypography>

                  <Divider sx={{ borderColor: "#d8e6f7", my: 2 }} />

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={addForm.email}
                        onChange={handleAddFormChange("email")}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Display Name (optional)"
                        value={addForm.displayName}
                        onChange={handleAddFormChange("displayName")}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField
                        select
                        fullWidth
                        label="Role"
                        value={addForm.role}
                        onChange={handleAddFormChange("role")}
                        sx={inputSx}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <VuiButton
                        color="info"
                        fullWidth
                        onClick={handleAddAllowedEmail}
                        disabled={isAddingEmail || emailsLoading}
                        sx={{ height: "100%" }}
                      >
                        {isAddingEmail ? "Adding..." : "Add"}
                      </VuiButton>
                    </Grid>
                  </Grid>

                  <Divider sx={{ borderColor: "#d8e6f7", my: 2 }} />

                  {emailsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {emailsError}
                    </Alert>
                  ) : null}

                  {emailsLoading ? (
                    <LoadingSpinner label="Loading allowed emails…" />
                  ) : allowedEmails.length === 0 ? (
                    <VuiTypography sx={{ color: TC.secondary }}>
                      No allowed SSO emails configured yet.
                    </VuiTypography>
                  ) : (
                    <Grid container spacing={2}>
                      {allowedEmails.map((entry) => {
                        const isRowLoading = Boolean(rowLoadingState[entry.email]);

                        return (
                          <Grid item xs={12} key={entry.email}>
                            <VuiBox
                              p={2}
                              sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "14px" }}
                            >
                              <VuiBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", md: "center" }}
                                gap={1.5}
                                flexDirection={{ xs: "column", md: "row" }}
                              >
                                <VuiBox sx={{ minWidth: 0 }}>
                                  <VuiTypography sx={{ color: TC.primary, fontWeight: 600, wordBreak: "break-word" }}>
                                    {entry.email}
                                  </VuiTypography>
                                  <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
                                    {entry.displayName || "No display name"}
                                  </VuiTypography>
                                </VuiBox>

                                <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Chip
                                    label={getRoleLabel(entry.role)}
                                    size="small"
                                    sx={{ background: "#e6f0ff", color: TC.primary, fontWeight: 600 }}
                                  />
                                  <TextField
                                    select
                                    size="small"
                                    label="Role"
                                    value={entry.role}
                                    onChange={(event) => handleRoleChange(entry.email, event.target.value)}
                                    disabled={isRowLoading}
                                    sx={{ ...inputSx, minWidth: 160 }}
                                  >
                                    {ROLE_OPTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <IconButton
                                    aria-label={`Remove allowed email ${entry.email}`}
                                    onClick={() => handleRemoveEmail(entry.email)}
                                    disabled={isRowLoading}
                                    sx={{ color: "#d14343", border: "1px solid #f0c5c5" }}
                                  >
                                    <IoClose aria-hidden="true" />
                                  </IconButton>
                                </VuiBox>
                              </VuiBox>
                            </VuiBox>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </VuiBox>
      <Footer />

      <Dialog open={scimModalOpen} onClose={() => setScimModalOpen(false)} aria-labelledby="scim-token-title">
        <DialogTitle id="scim-token-title">SCIM token generated</DialogTitle>
        <DialogContent>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Copy this token now — it will not be shown again. Store it securely (secrets manager or encrypted tenant settings).</VuiTypography>
          <VuiBox mt={2} sx={{ background: '#f4f8ff', p: 2, borderRadius: 1, wordBreak: 'break-all' }}>
            <VuiTypography variant="button" sx={{ color: TC.primary }}>{scimToken || '—'}</VuiTypography>
          </VuiBox>
        </DialogContent>
        <DialogActions>
          <VuiButton color="white" size="small" onClick={() => { try { navigator.clipboard.writeText(scimToken || ''); setStatus({ type: 'success', message: 'SCIM token copied to clipboard.' }); } catch (e) { setStatus({ type: 'error', message: 'Failed to copy token.' }); } }}>Copy</VuiButton>
          <VuiButton color="text" size="small" onClick={() => setScimModalOpen(false)}>Close</VuiButton>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default SSOSettings;

